import { useState, useCallback, useRef } from 'react';
import { apiClient, InventoryCluster, InventoryNamespace, AWSConnection, OCIConnection, CloudResource, FlowSummary } from '../../../api/client';
import {
    ClusterCacheEntry,
    NamespaceCacheEntry,
    AwsAccountCacheEntry,
    OciTenancyCacheEntry,
    groupResourcesByVpc,
    groupResourcesByVcn,
    buildIpToResourceMap,
} from '../types';
import {
    fetchClusterFlows,
    fetchNamespaceFlows,
    fetchClusterL7AccessLogs,
    fetchNamespaceL7AccessLogs,
} from '../utils';
import { CACHE } from '../constants';

// ============================================================================
// TYPES
// ============================================================================

export interface UseTopologyCacheResult {
    // Cache state
    clusterCache: Map<string, ClusterCacheEntry>;
    namespaceCache: Map<string, NamespaceCacheEntry>;
    awsAccountCache: Map<string, AwsAccountCacheEntry>;
    ociTenancyCache: Map<string, OciTenancyCacheEntry>;

    // Loading state
    loadingNodes: Set<string>;

    // Load functions
    loadClusterData: (clusterUID: string, cluster: InventoryCluster) => Promise<ClusterCacheEntry>;
    loadNamespaceData: (namespaceID: string, namespace: InventoryNamespace, clusterUID: string) => Promise<NamespaceCacheEntry>;
    loadAwsAccountData: (accountId: string, connection: AWSConnection) => Promise<AwsAccountCacheEntry>;
    loadOciTenancyData: (tenancyOcid: string, connection: OCIConnection) => Promise<OciTenancyCacheEntry>;

    // Cache utilities
    isClusterLoaded: (clusterUID: string) => boolean;
    isNamespaceLoaded: (namespaceID: string) => boolean;
    isAwsAccountLoaded: (accountId: string) => boolean;
    isOciTenancyLoaded: (tenancyOcid: string) => boolean;
    isNodeLoading: (nodeId: string) => boolean;
    invalidateCluster: (clusterUID: string) => void;
    invalidateNamespace: (namespaceID: string) => void;
    invalidateAwsAccount: (accountId: string) => void;
    invalidateOciTenancy: (tenancyOcid: string) => void;
    clearCache: () => void;

    // Error state
    errors: Map<string, string>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useTopologyCache(): UseTopologyCacheResult {
    // =========================================================================
    // STATE
    // =========================================================================

    const [clusterCache, setClusterCache] = useState<Map<string, ClusterCacheEntry>>(
        () => new Map()
    );

    const [namespaceCache, setNamespaceCache] = useState<Map<string, NamespaceCacheEntry>>(
        () => new Map()
    );

    const [awsAccountCache, setAwsAccountCache] = useState<Map<string, AwsAccountCacheEntry>>(
        () => new Map()
    );

    const [ociTenancyCache, setOciTenancyCache] = useState<Map<string, OciTenancyCacheEntry>>(
        () => new Map()
    );

    const [loadingNodes, setLoadingNodes] = useState<Set<string>>(
        () => new Set()
    );

    const [errors, setErrors] = useState<Map<string, string>>(
        () => new Map()
    );

    // Track in-flight requests to prevent duplicate calls
    const inFlightRequests = useRef<Map<string, Promise<any>>>(new Map());

    // =========================================================================
    // LOADING STATE HELPERS
    // =========================================================================

    const addLoadingNode = useCallback((nodeId: string) => {
        setLoadingNodes(prev => {
            const next = new Set(prev);
            next.add(nodeId);
            return next;
        });
    }, []);

    const removeLoadingNode = useCallback((nodeId: string) => {
        setLoadingNodes(prev => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
        });
    }, []);

    const addError = useCallback((nodeId: string, error: string) => {
        setErrors(prev => {
            const next = new Map(prev);
            next.set(nodeId, error);
            return next;
        });
    }, []);

    const clearError = useCallback((nodeId: string) => {
        setErrors(prev => {
            const next = new Map(prev);
            next.delete(nodeId);
            return next;
        });
    }, []);

    // =========================================================================
    // CACHE VALIDITY CHECKS
    // =========================================================================

    const isCacheValid = useCallback((loadedAt: number): boolean => {
        return Date.now() - loadedAt < CACHE.ttl;
    }, []);

    const isClusterLoaded = useCallback((clusterUID: string): boolean => {
        const entry = clusterCache.get(clusterUID);
        return entry?.isComplete === true && isCacheValid(entry.loadedAt);
    }, [clusterCache, isCacheValid]);

    const isNamespaceLoaded = useCallback((namespaceID: string): boolean => {
        const entry = namespaceCache.get(namespaceID);
        return entry?.isComplete === true && isCacheValid(entry.loadedAt);
    }, [namespaceCache, isCacheValid]);

    const isAwsAccountLoaded = useCallback((accountId: string): boolean => {
        const entry = awsAccountCache.get(accountId);
        return entry?.isComplete === true && isCacheValid(entry.loadedAt);
    }, [awsAccountCache, isCacheValid]);

    const isOciTenancyLoaded = useCallback((tenancyOcid: string): boolean => {
        const entry = ociTenancyCache.get(tenancyOcid);
        return entry?.isComplete === true && isCacheValid(entry.loadedAt);
    }, [ociTenancyCache, isCacheValid]);

    const isNodeLoading = useCallback((nodeId: string): boolean => {
        return loadingNodes.has(nodeId);
    }, [loadingNodes]);

    // =========================================================================
    // LOAD CLUSTER DATA
    // =========================================================================

    const loadClusterData = useCallback(async (
        clusterUID: string,
        cluster: InventoryCluster
    ): Promise<ClusterCacheEntry> => {
        // Return cached data if valid
        const cached = clusterCache.get(clusterUID);
        if (cached?.isComplete && isCacheValid(cached.loadedAt)) {
            return cached;
        }

        // Check for in-flight request
        const inFlight = inFlightRequests.current.get(`cluster:${clusterUID}`);
        if (inFlight) {
            return inFlight;
        }

        // Create new request
        addLoadingNode(clusterUID);
        clearError(clusterUID);

        const request = (async () => {
            try {
                // Parallel fetch all required data
                const [namespacesRes, nodesRes, flows, l7Logs] = await Promise.all([
                    apiClient.getInventoryNamespaces(clusterUID),
                    apiClient.getInventoryNodes(clusterUID).catch(err => {
                        console.warn(`[Cache] Nodes not available for cluster ${clusterUID}:`, err);
                        return { nodes: [] };
                    }),
                    fetchClusterFlows(clusterUID, 5000, 'namespace'),
                    fetchClusterL7AccessLogs(clusterUID, 3000, 'namespace').catch(err => {
                        console.warn(`[Cache] L7 logs not available for cluster ${clusterUID}:`, err);
                        return [];
                    })
                ]);

                const entry: ClusterCacheEntry = {
                    cluster,
                    namespaces: namespacesRes.namespaces || [],
                    nodes: nodesRes.nodes || [],
                    flows,
                    l7Logs,
                    loadedAt: Date.now(),
                    isComplete: true
                };

                // Update cache
                setClusterCache(prev => {
                    const next = new Map(prev);
                    next.set(clusterUID, entry);
                    return next;
                });

                return entry;
            } catch (err: any) {
                console.error(`[Cache] Error loading cluster ${clusterUID}:`, err);
                addError(clusterUID, err.message || 'Failed to load cluster data');
                throw err;
            } finally {
                removeLoadingNode(clusterUID);
                inFlightRequests.current.delete(`cluster:${clusterUID}`);
            }
        })();

        inFlightRequests.current.set(`cluster:${clusterUID}`, request);
        return request;
    }, [clusterCache, isCacheValid, addLoadingNode, removeLoadingNode, clearError, addError]);

    // =========================================================================
    // LOAD NAMESPACE DATA
    // =========================================================================

    const loadNamespaceData = useCallback(async (
        namespaceID: string,
        namespace: InventoryNamespace,
        clusterUID: string
    ): Promise<NamespaceCacheEntry> => {
        // Return cached data if valid
        const cached = namespaceCache.get(namespaceID);
        if (cached?.isComplete && isCacheValid(cached.loadedAt)) {
            return cached;
        }

        // Check for in-flight request
        const inFlight = inFlightRequests.current.get(`namespace:${namespaceID}`);
        if (inFlight) {
            return inFlight;
        }

        // Create new request
        addLoadingNode(namespaceID);
        clearError(namespaceID);

        const request = (async () => {
            try {
                // Parallel fetch all required data
                const [workloadsRes, servicesRes, flows, l7Logs] = await Promise.all([
                    apiClient.getInventoryWorkloads(namespaceID),
                    apiClient.getInventoryServices(namespaceID),
                    fetchNamespaceFlows(namespaceID, 2000),
                    fetchNamespaceL7AccessLogs(namespaceID, 1000).catch(err => {
                        console.warn(`[Cache] L7 logs not available for namespace ${namespaceID}:`, err);
                        return [];
                    })
                ]);

                const entry: NamespaceCacheEntry = {
                    namespace,
                    clusterUID,
                    workloads: workloadsRes.workloads || [],
                    services: servicesRes.services || [],
                    flows,
                    l7Logs,
                    loadedAt: Date.now(),
                    isComplete: true
                };

                // Update cache
                setNamespaceCache(prev => {
                    const next = new Map(prev);
                    next.set(namespaceID, entry);
                    return next;
                });

                return entry;
            } catch (err: any) {
                addError(namespaceID, err.message || 'Failed to load namespace data');
                throw err;
            } finally {
                removeLoadingNode(namespaceID);
                inFlightRequests.current.delete(`namespace:${namespaceID}`);
            }
        })();

        inFlightRequests.current.set(`namespace:${namespaceID}`, request);
        return request;
    }, [namespaceCache, isCacheValid, addLoadingNode, removeLoadingNode, clearError, addError]);

    // =========================================================================
    // LOAD AWS ACCOUNT DATA
    // =========================================================================

    const loadAwsAccountData = useCallback(async (
        accountId: string,
        connection: AWSConnection
    ): Promise<AwsAccountCacheEntry> => {
        // Return cached data if valid
        const cached = awsAccountCache.get(accountId);
        if (cached?.isComplete && isCacheValid(cached.loadedAt)) {
            return cached;
        }

        // Check for in-flight request
        const inFlight = inFlightRequests.current.get(`aws-account:${accountId}`);
        if (inFlight) {
            return inFlight;
        }

        // Create new request
        addLoadingNode(`aws-account:${accountId}`);
        clearError(`aws-account:${accountId}`);

        const request = (async () => {
            try {
                // Fetch cloud resources for this account with pagination
                const PAGE_SIZE = 100;
                let allResources: CloudResource[] = [];
                let currentPage = 1;
                let totalPages = 1;

                // Fetch first page
                const firstPageRes = await apiClient.getCloudResources({
                    account_id: accountId,
                    status: 'active',
                    page: currentPage,
                    page_size: PAGE_SIZE
                });

                allResources = firstPageRes.resources || [];
                totalPages = firstPageRes.total_pages || 1;

                // Fetch remaining pages if any
                while (currentPage < totalPages) {
                    currentPage++;
                    const pageRes = await apiClient.getCloudResources({
                        account_id: accountId,
                        status: 'active',
                        page: currentPage,
                        page_size: PAGE_SIZE
                    });
                    if (pageRes.resources && pageRes.resources.length > 0) {
                        allResources = [...allResources, ...pageRes.resources];
                    }
                }

                // Filter out resources that don't belong on an infrastructure topology map
                const EXCLUDED_RESOURCE_TYPES = [
                    // Network infrastructure (VPCs are containers, subnets/SGs are too low-level)
                    'AWS::EC2::VPC',
                    'AWS::EC2::Subnet',
                    'AWS::EC2::SecurityGroup',
                    // IAM resources (identity/permissions, not infrastructure)
                    'AWS::IAM::User',
                    'AWS::IAM::Role',
                    'AWS::IAM::Policy',
                    'AWS::IAM::Group',
                    // Config/monitoring resources
                    'AWS::KMS::Key',
                    'AWS::CloudWatch::Alarm',
                    'AWS::CloudTrail::Trail',
                ];

                const resources = allResources.filter(
                    r => !EXCLUDED_RESOURCE_TYPES.includes(r.resource_type)
                );

                // Group resources by VPC
                const vpcGroups = groupResourcesByVpc(resources);

                // Extract unique VPC IDs (excluding 'global')
                const vpcIds = vpcGroups
                    .filter(g => !g.isGlobal)
                    .map(g => g.vpcId);

                // Build IP-to-resource lookup map for VPC Flow Log correlation
                const ipToResourceMap = buildIpToResourceMap(resources);

                // Fetch VPC Flow Log summaries for this account (if flow logs are enabled)
                let flows: FlowSummary[] = [];
                try {
                    const flowsRes = await apiClient.getAWSAccountFlows(connection.id, 1000);
                    flows = flowsRes.flows || [];
                } catch (flowError) {
                    // Flow logs may not be enabled for this account - this is not an error
                    console.debug(`[loadAwsAccountData] No flows for account ${accountId}:`, flowError);
                }

                const entry: AwsAccountCacheEntry = {
                    connection,
                    accountId,
                    resources,
                    vpcGroups,
                    vpcIds,
                    flows,
                    ipToResourceMap,
                    loadedAt: Date.now(),
                    isComplete: true
                };

                // Update cache
                setAwsAccountCache(prev => {
                    const next = new Map(prev);
                    next.set(accountId, entry);
                    return next;
                });

                return entry;
            } catch (err: any) {
                addError(`aws-account:${accountId}`, err.message || 'Failed to load AWS account data');
                throw err;
            } finally {
                removeLoadingNode(`aws-account:${accountId}`);
                inFlightRequests.current.delete(`aws-account:${accountId}`);
            }
        })();

        inFlightRequests.current.set(`aws-account:${accountId}`, request);
        return request;
    }, [awsAccountCache, isCacheValid, addLoadingNode, removeLoadingNode, clearError, addError]);

    // =========================================================================
    // LOAD OCI TENANCY DATA
    // =========================================================================

    const loadOciTenancyData = useCallback(async (
        tenancyOcid: string,
        connection: OCIConnection
    ): Promise<OciTenancyCacheEntry> => {
        // Return cached data if valid
        const cached = ociTenancyCache.get(tenancyOcid);
        if (cached?.isComplete && isCacheValid(cached.loadedAt)) {
            return cached;
        }

        // Check for in-flight request
        const inFlight = inFlightRequests.current.get(`oci-tenancy:${tenancyOcid}`);
        if (inFlight) {
            return inFlight;
        }

        // Create new request
        addLoadingNode(`oci-tenancy:${tenancyOcid}`);
        clearError(`oci-tenancy:${tenancyOcid}`);

        const request = (async () => {
            try {
                // Fetch OCI cloud resources for this tenancy
                const resourcesRes = await apiClient.getOCIResources({
                    status: 'active'
                });

                // Filter to this tenancy's resources
                const allResources = (resourcesRes.resources || []).filter(
                    r => r.oci_tenancy_ocid === tenancyOcid
                );

                // Filter out resources that don't belong on an infrastructure topology map
                const EXCLUDED_RESOURCE_TYPES = [
                    // Network infrastructure (VCNs are containers, not resources to display)
                    'OCI::Core::VCN',
                    'OCI::Core::Vcn',
                    // Subnets are grouping containers, not individual resources
                    'OCI::Core::Subnet',
                    // Security config objects, not infrastructure resources
                    'OCI::Core::SecurityList',
                    'OCI::Core::NetworkSecurityGroup',
                    'OCI::Core::RouteTable',
                    // Identity resources (identity/permissions, not infrastructure)
                    'OCI::Identity::User',
                    'OCI::Identity::Group',
                    'OCI::Identity::Policy',
                    'OCI::Identity::Compartment',
                ];

                const resources = allResources.filter(
                    r => !EXCLUDED_RESOURCE_TYPES.includes(r.resource_type)
                );

                // Group resources by VCN
                const vcnGroups = groupResourcesByVcn(resources);

                // Extract unique VCN IDs (excluding 'global')
                const vcnIds = vcnGroups
                    .filter(g => !g.isGlobal)
                    .map(g => g.vcnId);

                // Build IP to resource mapping for flow log correlation
                const ipToResourceMap = buildIpToResourceMap(resources);

                // Fetch VCN Flow Log summaries for this tenancy (if flow logs are enabled)
                let flows: FlowSummary[] = [];
                try {
                    const flowsRes = await apiClient.getOCITenancyFlows(connection.id, 1000);
                    flows = flowsRes.flows || [];
                } catch (flowError) {
                    // Flow logs may not be enabled for this tenancy - this is not an error
                    console.debug(`[loadOciTenancyData] No flows for tenancy ${tenancyOcid}:`, flowError);
                }

                const entry: OciTenancyCacheEntry = {
                    connection,
                    tenancyOcid,
                    resources,
                    vcnGroups,
                    vcnIds,
                    flows,
                    ipToResourceMap,
                    loadedAt: Date.now(),
                    isComplete: true
                };

                // Update cache
                setOciTenancyCache(prev => {
                    const next = new Map(prev);
                    next.set(tenancyOcid, entry);
                    return next;
                });

                return entry;
            } catch (err: any) {
                addError(`oci-tenancy:${tenancyOcid}`, err.message || 'Failed to load OCI tenancy data');
                throw err;
            } finally {
                removeLoadingNode(`oci-tenancy:${tenancyOcid}`);
                inFlightRequests.current.delete(`oci-tenancy:${tenancyOcid}`);
            }
        })();

        inFlightRequests.current.set(`oci-tenancy:${tenancyOcid}`, request);
        return request;
    }, [ociTenancyCache, isCacheValid, addLoadingNode, removeLoadingNode, clearError, addError]);

    // =========================================================================
    // CACHE INVALIDATION
    // =========================================================================

    const invalidateCluster = useCallback((clusterUID: string) => {
        setClusterCache(prev => {
            const next = new Map(prev);
            next.delete(clusterUID);
            return next;
        });
    }, []);

    const invalidateNamespace = useCallback((namespaceID: string) => {
        setNamespaceCache(prev => {
            const next = new Map(prev);
            next.delete(namespaceID);
            return next;
        });
    }, []);

    const invalidateAwsAccount = useCallback((accountId: string) => {
        setAwsAccountCache(prev => {
            const next = new Map(prev);
            next.delete(accountId);
            return next;
        });
    }, []);

    const invalidateOciTenancy = useCallback((tenancyOcid: string) => {
        setOciTenancyCache(prev => {
            const next = new Map(prev);
            next.delete(tenancyOcid);
            return next;
        });
    }, []);

    const clearCache = useCallback(() => {
        setClusterCache(new Map());
        setNamespaceCache(new Map());
        setAwsAccountCache(new Map());
        setOciTenancyCache(new Map());
        setErrors(new Map());
        inFlightRequests.current.clear();
    }, []);

    // =========================================================================
    // RETURN
    // =========================================================================

    return {
        clusterCache,
        namespaceCache,
        awsAccountCache,
        ociTenancyCache,
        loadingNodes,
        loadClusterData,
        loadNamespaceData,
        loadAwsAccountData,
        loadOciTenancyData,
        isClusterLoaded,
        isNamespaceLoaded,
        isAwsAccountLoaded,
        isOciTenancyLoaded,
        isNodeLoading,
        invalidateCluster,
        invalidateNamespace,
        invalidateAwsAccount,
        invalidateOciTenancy,
        clearCache,
        errors
    };
}
